/**
 * HrV1AppealsIndex.tsx
 *
 * Cross-cycle list of Appeals. Surfaces every open appeal across all
 * hr_v1 cycles, plus a tab for decided appeals (for HR's audit).
 *
 * Route: /hr-v1/appeals (HR-role only)
 */

import React, { useCallback, useEffect, useState } from 'react';
import Button from '../components/ui/Button';
import CenterMessage from '../components/ui/CenterMessage';
import DisclosureBadge from '../components/epr/DisclosureBadge';
import { useToast } from '../components/ui/Toast';
import { useAuth } from '../contexts/AuthContext';
import {
  collection, getDocs, query, where,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import type {
  AppealRecord, AppealStatus, AppealDecision,
} from '../types/appeal';
import type { ReviewCycle } from '../types';
import { decideAppeal } from '../services/appealService';

interface Row {
  appeal: AppealRecord;
  cycleName?: string;
}

const HrV1AppealsIndex: React.FC = () => {
  const toast = useToast();
  const { user } = useAuth();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'open' | 'decided'>('open');
  const [decidingId, setDecidingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // All hr_v1 cycles → cycle name lookup
      const cyclesSnap = await getDocs(
        query(collection(db, 'reviewCycles'), where('frameworkVersion', '==', 'hr_v1')),
      );
      const cycleNames: Record<string, string> = {};
      cyclesSnap.docs.forEach(d => {
        cycleNames[d.id] = (d.data() as ReviewCycle).name;
      });
      const cycleIds = cyclesSnap.docs.map(d => d.id);
      if (cycleIds.length === 0) {
        setRows([]); return;
      }
      const statuses: AppealStatus[] = tab === 'open' ? ['open', 'in_meeting'] : ['decided'];
      const out: Row[] = [];
      for (const chunk of chunkArr(cycleIds, 10)) {
        try {
          const q = query(
            collection(db, 'appealRecords'),
            where('cycleId', 'in', chunk),
            where('status', 'in', statuses),
          );
          const snap = await getDocs(q);
          for (const d of snap.docs) {
            const a = { id: d.id, ...(d.data() as Omit<AppealRecord, 'id'>) };
            out.push({ appeal: a, cycleName: cycleNames[a.cycleId] });
          }
        } catch (chunkErr) {
          console.warn('[appeals] chunk load failed, skipping:', chunkErr);
        }
      }
      out.sort((x, y) => {
        const tx = x.appeal.filedAt?.toMillis?.() ?? 0;
        const ty = y.appeal.filedAt?.toMillis?.() ?? 0;
        return ty - tx;
      });
      setRows(out);
    } catch (e) {
      toast.show(`Failed to load: ${(e as Error).message}`, { icon: 'error' });
    } finally {
      setLoading(false);
    }
  }, [tab, toast]);

  useEffect(() => { void load(); }, [load]);

  const handleDecide = useCallback(async (
    appealId: string,
    decision: AppealDecision,
    notes: string,
  ) => {
    setDecidingId(appealId);
    try {
      await decideAppeal({
        appealId,
        decision,
        decisionBy: user?.uid ?? 'unknown',
        decisionNotes: notes,
      });
      toast.show('Appeal decided.', { icon: 'check' });
      void load();
    } catch (e) {
      toast.show(`Failed: ${(e as Error).message}`, { icon: 'error' });
    } finally {
      setDecidingId(null);
    }
  }, [user?.uid, toast, load]);

  if (loading) return <CenterMessage tone="info" title="Loading appeals..." />;

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-stone-500">HR Console</div>
          <h1 className="text-2xl font-semibold text-stone-900 tracking-tight">Appeals</h1>
          <p className="text-sm text-stone-600 mt-1">
            Employees who do not agree with their appraisal result. Filing window: 2 days after manager sign-off.
          </p>
        </div>
        <div className="flex gap-2">
          <TabButton active={tab === 'open'} onClick={() => setTab('open')}>
            Open / In Meeting
          </TabButton>
          <TabButton active={tab === 'decided'} onClick={() => setTab('decided')}>
            Decided
          </TabButton>
        </div>
      </header>

      {rows.length === 0 ? (
        <CenterMessage
          tone={tab === 'open' ? 'success' : 'info'}
          title={tab === 'open' ? 'No open appeals' : 'No decided appeals yet'}
          body={
            tab === 'open'
              ? 'Cycle can continue to Step 7 (Outcome) without blockers.'
              : 'Decided appeals will appear here for audit.'
          }
        />
      ) : (
        <div className="space-y-3">
          {rows.map(row => (
            <AppealCard
              key={row.appeal.id}
              row={row}
              busy={decidingId === row.appeal.id}
              onDecide={(decision, notes) => handleDecide(row.appeal.id, decision, notes)}
              readOnly={tab === 'decided'}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Subcomponents ────────────────────────────────────────────────

const TabButton: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode }> = ({
  active, onClick, children,
}) => (
  <button
    type="button"
    onClick={onClick}
    className={[
      'px-3 py-1.5 text-sm rounded-lg border',
      active
        ? 'bg-stone-900 text-white border-stone-900'
        : 'bg-white text-stone-700 border-stone-300 hover:border-stone-500',
    ].join(' ')}
  >
    {children}
  </button>
);

const AppealCard: React.FC<{
  row: Row;
  busy: boolean;
  readOnly: boolean;
  onDecide: (decision: AppealDecision, notes: string) => void;
}> = ({ row, busy, readOnly, onDecide }) => {
  const [notes, setNotes] = useState('');
  const a = row.appeal;

  return (
    <section className="rounded-xl border border-stone-200 bg-white p-5">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h3 className="text-base font-semibold text-stone-900">
              Employee <code>{a.employeeId}</code>
            </h3>
            <DisclosureBadge
              icon={a.status === 'decided' ? 'check_circle' : a.status === 'in_meeting' ? 'forum' : 'pending'}
              label={`status: ${a.status}`}
              tone={a.status === 'decided' ? 'success' : a.status === 'in_meeting' ? 'info' : 'warning'}
            />
            {a.decision && (
              <DisclosureBadge
                icon="gavel"
                label={`decision: ${a.decision.replace(/_/g, ' ')}`}
                tone="info"
              />
            )}
          </div>
          <div className="text-xs text-stone-500">
            Cycle: <code>{row.cycleName ?? a.cycleId}</code>
            {' · '}
            Filed: {a.filedAt?.toDate ? a.filedAt.toDate().toLocaleString('en-GB') : '—'}
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-stone-200 bg-stone-50 p-3 text-sm mb-3">
        <div className="text-xs font-medium text-stone-500 mb-1">Alasan appeal:</div>
        <div className="whitespace-pre-wrap text-stone-800">{a.filedReason}</div>
      </div>

      {a.decisionNotes && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm mb-3">
          <div className="text-xs font-medium text-emerald-700 mb-1">Catatan keputusan HR:</div>
          <div className="whitespace-pre-wrap text-emerald-900">{a.decisionNotes}</div>
        </div>
      )}

      {!readOnly && (
        <>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="Catatan keputusan (opsional, tapi disarankan untuk audit)"
            rows={2}
            className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-stone-400 mb-3"
          />
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="secondary" loading={busy} onClick={() => onDecide('upheld_original', notes)}>
              Uphold original
            </Button>
            <Button variant="secondary" loading={busy} onClick={() => onDecide('partially_adjusted', notes)}>
              Partially adjust
            </Button>
            <Button variant="submit" loading={busy} onClick={() => onDecide('fully_revised', notes)}>
              Fully revise
            </Button>
          </div>
        </>
      )}
    </section>
  );
};

// ─── Helpers ──────────────────────────────────────────────────────

function chunkArr<T>(arr: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

export default HrV1AppealsIndex;
